import React, { useContext } from 'react';
import { useHistory } from 'react-router-dom';
import PopupMenu from 'components/UiKit/PopupMenu';
import { Button, ButtonType } from 'syngenta-digital-cropwise-react-ui-kit';
import { AddRounded } from '@material-ui/icons';
import PropTypes from 'prop-types';
import { useFlags } from 'launchdarkly-react-client-sdk';

import EditOutlined from 'assets/edit_outlined.svg';
import BulkActions from 'assets/bulk_actions.svg';
import { CropZoneIcon, FarmIcon, FieldsIcon } from 'components/Icons';
import { Context } from 'components/Store';
import featureFlagRenderItem from 'helpers/featureFlagRenderItem';
import { IS_MASS_ASSIGNER_ACTIVE, SELECT_PROPERTY } from 'reducers/reducer';
import { paths } from 'routes/paths';
import PropertyLandingContext from 'screens/Property/PropertiesLanding/context';
import useExportPDFMapsActions from 'screens/Property/hooks/useExportPDFMapsActions';
import { AmplitudeContext } from 'utilities/amplitude/useAmplitude';
import { useAuth } from 'utilities/base-auth';

const Actions = ({ onCreateFarm, openCreateModal, setView }) => {
  const history = useHistory();
  const { user } = useAuth();

  const amplitude = useContext(AmplitudeContext);
  const [
    {
      selectedProperty,
      selectedFieldRowId,
      organization: { id: orgId }
    },
    dispatch
  ] = useContext(Context);
  const {
    propertyLandingPageData,
    isFarmSelected,
    guardNavigation
  } = useContext(PropertyLandingContext);

  const { initiateExportFlow } = useExportPDFMapsActions(dispatch);

  const {
    releaseFePropertyLandingBulkActions,
    releaseFePropertyLandingBulkActionsByOrg,
    experimentFeExportPdfMapsMapControlByUserId
  } = useFlags();

  const getFieldId = () => {
    if (selectedProperty?.type === 'Field') return selectedProperty.id;
    return selectedFieldRowId.id;
  };

  // Bulk edit clears the selection and switches view, which closes an open
  // crop zone form. Guarding the whole action rather than the view change
  // alone: the dispatches below would otherwise still run behind the prompt.
  const handleMassAssigner = () => {
    guardNavigation(() => {
      if (setView) {
        amplitude.sendEventToAmplitude(
          amplitude.events.epic.Properties.initiateBulkEdit
        );
        setView('map');
      }
      dispatch({
        type: SELECT_PROPERTY,
        payload: {}
      });
      dispatch({
        type: IS_MASS_ASSIGNER_ACTIVE,
        payload: true
      });
    });
  };

  const handleExportPDFMaps = () => {
    guardNavigation(() => {
      if (setView) {
        setView('map');
      }
      amplitude.sendEventToAmplitude(
        amplitude.events.epic.Properties.accessPropertyMaps,
        {
          from: 'bulk actions dropdown'
        }
      );
      initiateExportFlow();
    });
  };

  const getParentPage = () => {
    if (isFarmSelected) return selectedProperty?.parentPage;
    return (propertyLandingPageData?.properties || []).find(
      property => property.id === selectedProperty?.farmId
    )?.parentPage;
  };

  const isBulkActionsEnabled =
    featureFlagRenderItem(
      releaseFePropertyLandingBulkActions,
      releaseFePropertyLandingBulkActionsByOrg,
      orgId
    ) && !experimentFeExportPdfMapsMapControlByUserId?.includes(user.id);

  return (
    <div className="flex justify-between" data-testid="properties-actions">
      {isBulkActionsEnabled ? (
        <PopupMenu
          className="popup-width mr-2"
          trigger={
            <Button
              style={{ width: '149px', padding: '0 22px' }}
              icon={<img width={18} src={BulkActions} alt="edit" />}
              type={ButtonType.outline}
            >
              Bulk Actions
            </Button>
          }
        >
          <PopupMenu.List>
            <PopupMenu.Item onClick={handleMassAssigner}>
              <span className="ml-2">Bulk Edit</span>
            </PopupMenu.Item>
            <PopupMenu.Item onClick={handleExportPDFMaps}>
              <span className="ml-2">Export PDF Maps</span>
            </PopupMenu.Item>
          </PopupMenu.List>
        </PopupMenu>
      ) : (
        <Button
          style={{ width: '149px', padding: '0 32px' }}
          icon={<img width={18} src={EditOutlined} alt="edit" />}
          type={ButtonType.outline}
          onClick={handleMassAssigner}
        >
          Bulk Edit
        </Button>
      )}

      <PopupMenu
        className="popup-width"
        trigger={
          <Button
            style={{ width: '149px' }}
            icon={<AddRounded />}
            type={ButtonType.success}
            ghost
          >
            New Property
          </Button>
        }
      >
        <PopupMenu.List>
          <PopupMenu.Item onClick={onCreateFarm}>
            <FarmIcon />
            <span className="ml-2">Farm</span>
          </PopupMenu.Item>
          <PopupMenu.Item onClick={() => openCreateModal(true)}>
            <FieldsIcon />
            <span className="ml-2">Field</span>
          </PopupMenu.Item>
          {(selectedProperty?.type === 'Field' ||
            selectedProperty?.type === 'Crop Zone') && (
            <PopupMenu.Item
              onClick={() => {
                history.push(
                  `${paths.properties}/create/${getFieldId()}/cropzone`,
                  {
                    parentPage: getParentPage()
                  }
                );
              }}
            >
              <CropZoneIcon />
              <span className="ml-2">Crop Zone</span>
            </PopupMenu.Item>
          )}
        </PopupMenu.List>
      </PopupMenu>
    </div>
  );
};

Actions.propTypes = {
  openCreateModal: PropTypes.func.isRequired,
  onCreateFarm: PropTypes.func.isRequired,
  setView: PropTypes.func.isRequired
};

export default Actions;
